// Integrações do seller (`spec/17-frontend.md` §8.1, `spec/18-integrations.md` §7, task
// WEB-43). Foco da tela: **o card da UTMify**. Quem tem `useQuery`/`useMutation` é esta
// rota; `features/integrations` recebe dado por prop.
//
// Sem step-up: `spec/18` §4.2, configurar tracking não move dinheiro.

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { createFileRoute } from '@tanstack/react-router';
import { useState } from 'react';
import {
  INTEGRATIONS_QUERY_KEY,
  type Integration,
  integrationQueryKey,
  removeUtmify,
  saveUtmifyToken,
  setUtmifyEnabled,
  testUtmify,
  utmifyQueryOptions,
} from '../api/queries/integrations.ts';
import { useMode } from '../app/mode.tsx';
import {
  type TestMessage,
  testErrorMessage,
  testResultMessage,
} from '../features/integrations/model.ts';
import { UtmifyCard } from '../features/integrations/utmify-card.tsx';

function Integrations() {
  const { mode } = useMode();
  const queryClient = useQueryClient();
  const integration = useQuery(utmifyQueryOptions(mode));
  const [testResult, setTestResult] = useState<TestMessage | undefined>(undefined);

  // `PUT` e `PATCH` devolvem o objeto inteiro: escrever no cache evita um GET a mais e
  // mantém a tela consistente mesmo se a rede cair logo depois.
  const write = (data: Integration): void => {
    queryClient.setQueryData(integrationQueryKey(mode), data);
  };

  const refresh = async (): Promise<void> => {
    await queryClient.invalidateQueries({ queryKey: [INTEGRATIONS_QUERY_KEY] });
  };

  const save = useMutation({
    mutationFn: async (token: string) => saveUtmifyToken(mode, token),
    onSuccess: (data) => {
      setTestResult(undefined);
      write(data);
    },
  });

  const toggle = useMutation({
    mutationFn: async (enabled: boolean) => setUtmifyEnabled(mode, enabled),
    onSuccess: write,
  });

  const remove = useMutation({
    mutationFn: async () => removeUtmify(mode),
    onSuccess: () => {
      setTestResult(undefined);
    },
    onSettled: refresh,
  });

  const test = useMutation({
    mutationFn: async () => testUtmify(mode),
    onSuccess: (result) => {
      setTestResult(testResultMessage(result));
    },
    onError: (error) => {
      setTestResult(testErrorMessage(error));
    },
    // Sem `invalidate`: `sendTest` do service manda o pedido e volta, não escreve
    // `last_ok_at`/`last_error` na linha (só o worker escreve, na entrega de verdade).
  });

  return (
    <>
      <UtmifyCard
        // Remonta depois de cada escrita aceita: é o que tira o formulário de "Trocar
        // token" da tela sem um `useEffect` espiando prop.
        key={integration.data?.updated_at ?? 'nova'}
        integration={integration.data}
        isLoading={integration.isPending}
        error={integration.data === undefined ? integration.error : undefined}
        onRetry={() => {
          void integration.refetch();
        }}
        onSave={(token) => {
          save.mutate(token);
        }}
        isSaving={save.isPending}
        saveError={save.error}
        onToggle={(enabled) => {
          toggle.mutate(enabled);
        }}
        isToggling={toggle.isPending}
        onTest={() => {
          test.mutate();
        }}
        isTesting={test.isPending}
        {...(testResult === undefined ? {} : { testResult })}
        onRemove={() => {
          remove.mutate();
        }}
        isRemoving={remove.isPending}
        actionError={toggle.error ?? remove.error}
      />
    </>
  );
}

export const Route = createFileRoute('/app/settings/integrations/')({ component: Integrations });
